import { Dialog, Header, Box, Text, IconButton, Button, Input, Chip, color } from 'folds';
import { ClipboardIcon, MapPinAreaIcon, MapPinLineIcon } from '@phosphor-icons/react';
import { chipIcon, composerIcon, Warning, X } from '$components/icons/phosphor';
import { readClipboardText } from '$utils/dom';
import type { IContent, Room } from 'matrix-js-sdk';
import * as css from './LocationDialog.css';
import { MapContainer, Marker, TileLayer, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useCallback, useEffect, useRef, useState, type ChangeEventHandler } from 'react';
import type { LatLngLiteral } from 'leaflet';
import L from 'leaflet';
import { MsgType } from '$types/matrix-sdk';
import { settingsAtom } from '$state/settings';
import { useSetting } from '$state/hooks/settings';
import classNames from 'classnames';
import markerIconPng from 'leaflet/dist/images/marker-icon.png';
import { Icon } from 'leaflet';
import { ModalOverlay } from '$components/modal-overlay/ModalOverlay';
import { getTileUrl, TILE_ATTRIBUTION } from '$utils/tileUrl';
import {
  getCurrentCoordinates,
  GeolocationError,
  type GeolocationFailure,
} from '$utils/geolocation';

const markerIcon = new Icon({
  iconUrl: markerIconPng,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
export function filterLocationString(result: string) {
  // OSM format
  if (result.toLowerCase().includes('lat=') && result.toLowerCase().includes('lon=')) {
    const tokenizedResult = result.toLowerCase().split(/[ ,&?;::m]/);
    const coords: { mlat?: string; mlng?: string } = {
      mlat: tokenizedResult.find((item) => item.includes('lat='))?.substring(4),
      mlng: tokenizedResult.find((item) => item.includes('lon='))?.substring(4),
    };
    if (coords.mlat && coords.mlng) {
      const mlat = Number.parseFloat(coords.mlat);
      const mlng = Number.parseFloat(coords.mlng);

      if (!Number.isNaN(mlat) && !Number.isNaN(mlng)) {
        return { status: LocationErrors.none, lat: mlat, lon: mlng } as LocationPoint;
      }
    }
  }
  // Fallback the center of the map on OSM
  if (result.toLowerCase().includes('#map=')) {
    const coords: string[] = result
      .substring(result.toLowerCase().indexOf('#map=') + 5)
      .split(/[, /]/);
    if (coords.length >= 3 && typeof coords[1] === 'string' && typeof coords[2] === 'string') {
      const mlat = Number.parseFloat(coords[1]);
      const mlng = Number.parseFloat(coords[2]);

      if (!Number.isNaN(mlat) && !Number.isNaN(mlng))
        return { status: LocationErrors.none, lat: mlat, lon: mlng } as LocationPoint;
    }
  }
  // apple address bar pins (eg. https://maps.apple.com/place?auid=6096426607790210541&address=Catal%C3%A3o+-+GO%2C+75714-000%2C+Brazil&coordinate=-17.711014%2C-47.488393&name=75714-000&lsp=7618 )
  if (result.toLowerCase().includes('&coordinate=')) {
    const coords: string[] = result
      .substring(result.toLowerCase().indexOf('&coordinate='))
      .replaceAll(/[=& ]|(%2C)/g, ' ')
      .trim()
      .split(' ')
      .filter((item) => item.length > 0);
    if (coords.length >= 3 && typeof coords[1] === 'string' && typeof coords[2] === 'string') {
      const mlat = Number.parseFloat(coords[1]);
      const mlng = Number.parseFloat(coords[2]);

      if (!Number.isNaN(mlat) && !Number.isNaN(mlng)) {
        return { status: LocationErrors.none, lat: mlat, lon: mlng } as LocationPoint;
      }
    }
  }
  // geo tags or just putting the numbers without any formatting next to the other
  const coords = result.split(/[, ;:]/).filter((item) => item.length && item !== 'geo');
  if (coords.length >= 2 && typeof coords[0] === 'string' && typeof coords[1] === 'string') {
    const mlat = Number.parseFloat(coords[0]);
    const mlng = Number.parseFloat(coords[1]);
    if (!Number.isNaN(mlat) && !Number.isNaN(mlng))
      return { status: LocationErrors.none, lat: mlat, lon: mlng } as LocationPoint;
  }
  return { status: LocationErrors.clipboard };
}

type LocationDialogProps = {
  onCancel: () => void;
  onSubmit: (content: IContent) => Promise<void>;
  room: Room;
};

export enum LocationErrors {
  none,
  permissions = 'You have denied Sable access to you location services',
  module = 'Your device does not have a gps module, or it may not be turned on',
  unsupported = 'Location services are unavailable on desktop, enter or paste the coordinates instead',
  unknown = 'The sharing failed for unknown reasons',
  clipboard = 'Unable to identify the coordinates from clipboard',
  missingClipboard = 'Unable to retrieve clipboard contents',
}

const FAILURE_ERRORS: Partial<Record<GeolocationFailure, LocationErrors>> = {
  permissions: LocationErrors.permissions,
  unavailable: LocationErrors.module,
  unsupported: LocationErrors.unsupported,
};

export type LocationPoint = {
  status: LocationErrors;
  lat?: number;
  lon?: number;
};

export function LocationDialog({ onCancel, onSubmit, room }: LocationDialogProps) {
  const [showInteractiveMap] = useSetting(settingsAtom, 'showInteractiveMap');
  const [showEncInteractiveMap] = useSetting(settingsAtom, 'showEncInteractiveMap');
  const showMaps = room.hasEncryptionStateEvent() ? showEncInteractiveMap : showInteractiveMap;

  const initCoords = { lat: 43.959971, lng: -59.790623 };
  const [inputPosition, setInputPosition] = useState<{ lat: string; lng: string }>({
    lat: initCoords.lat.toPrecision(6),
    lng: initCoords.lng.toPrecision(6),
  });
  const [pinPosition, setPinPosition] = useState<L.LatLngLiteral>(initCoords);
  const zoom = useRef<number>(2);
  const [locationError, setLocationError] = useState<LocationErrors>(LocationErrors.none);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [map, setMap] = useState<L.Map | null>(null);

  const onMove = useCallback(() => {
    if (map?.getCenter() && map?.getZoom()) {
      zoom.current = map?.getZoom();
    }
  }, [map]);

  useEffect(() => {
    map?.on('move', onMove);
    return () => {
      map?.off('move', onMove);
    };
  }, [map, onMove]);
  const movePin = useCallback(
    (pos: LatLngLiteral) => {
      if (!map) return;
      map.eachLayer((layer) => {
        if (layer instanceof L.Marker) {
          map.removeLayer(layer);
        }
      });
      const marker = L.marker(pos).addTo(map);
      marker.on({
        mousedown: (e) => {
          e.originalEvent.preventDefault();
          e.originalEvent.stopPropagation();
        },
      });
      marker.setIcon(markerIcon);
      map.panTo(pos);
    },
    [map]
  );
  const MapEvents = () => {
    useMapEvents({
      click(e) {
        // setPinPosition(e.latlng);
        if (map) {
          movePin(e.latlng);
          setInputPosition({ lat: e.latlng.lat.toFixed(6), lng: e.latlng.lng.toFixed(6) });
          setPinPosition({ lat: e.latlng.lat, lng: e.latlng.lng });
        }
      },
    });
    return false;
  };

  function storeLocation(coords: LocationPoint) {
    setLocationError(coords.status);
    if (!coords.lat || !coords.lon || coords.status !== LocationErrors.none) return;
    movePin({ lat: coords.lat, lng: coords.lon });
    setInputPosition({ lat: coords.lat.toFixed(6), lng: coords.lon.toFixed(6) });
    setPinPosition({ lat: coords.lat, lng: coords.lon });
    return;
  }

  function getClipboard() {
    readClipboardText()
      .then((result: string) => {
        const coords = filterLocationString(result);
        storeLocation(coords);
      })
      .catch(() => storeLocation({ status: LocationErrors.missingClipboard }));
  }

  async function getLocation() {
    try {
      const { lat, lon } = await getCurrentCoordinates();
      storeLocation({ lat, lon, status: LocationErrors.none });
    } catch (err) {
      setLocationError(
        (err instanceof GeolocationError && FAILURE_ERRORS[err.reason]) || LocationErrors.unknown
      );
    }
  }
  const handleLat: ChangeEventHandler<HTMLInputElement> = (evt) => {
    const val = evt.target.value;
    const parsed = Number.parseFloat(val);
    setInputPosition({ lat: val, lng: inputPosition.lng });
    if (!Number.isNaN(parsed) && parsed >= -90 && parsed <= 90) {
      movePin({ lat: parsed, lng: pinPosition.lng });
      setPinPosition({ lat: parsed, lng: pinPosition.lng });
    }
  };

  const handleLng: ChangeEventHandler<HTMLInputElement> = (evt) => {
    const val = evt.target.value;
    const parsed = Number.parseFloat(val);
    setInputPosition({ lat: inputPosition.lat, lng: val });
    if (!Number.isNaN(parsed) && parsed >= -180 && parsed <= 180) {
      movePin({ lat: pinPosition.lat, lng: parsed });
      setPinPosition({ lat: pinPosition.lat, lng: parsed });
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    const mlat = pinPosition.lat.toFixed(6);
    const mlon = pinPosition.lng.toFixed(6);
    const content: IContent = {
      msgtype: MsgType.Location,
      geo_uri: `geo:${mlat},${mlon};u=0`,
      body: `https://www.openstreetmap.org/?mlat=${mlat}&mlon=${mlon}#map=16/${mlat}/${mlon}"`,
    };
    setSubmitError(undefined);
    setIsSubmitting(true);
    try {
      await onSubmit(content);
      onCancel();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to share location. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalOverlay requestClose={onCancel}>
      <Dialog variant="Surface" className={css.LocationDialogBody}>
        <Header className={css.LocationDialogHeader} variant="Surface" size="500">
          <Box grow="Yes" gap="200">
            <MapPinLineIcon size="20" />
            <Text size="H4">Share Location</Text>
          </Box>
          <IconButton
            size="300"
            onClick={onCancel}
            radii="300"
            title="Cancel Sharing Location"
            aria-label="Cancel Sharing Location"
          >
            {composerIcon(X)}
          </IconButton>
        </Header>
        <Box direction="Column" gap="200" className={css.LocationDialogItems}>
          <Box direction="Row" className={css.LocationDialogButtons}>
            <Chip
              variant={
                locationError === LocationErrors.none ||
                locationError === LocationErrors.clipboard ||
                locationError === LocationErrors.missingClipboard
                  ? 'Primary'
                  : 'Critical'
              }
              className={classNames(css.LocationInputItem, css.LocationInputCurLocation)}
              onClick={() => void getLocation()}
              before={<MapPinAreaIcon size="18" />}
            >
              <Text className={css.LocationInputField}>Share Current Location</Text>
            </Chip>
            <Chip
              variant={
                locationError !== LocationErrors.clipboard &&
                locationError !== LocationErrors.missingClipboard
                  ? 'Secondary'
                  : 'Critical'
              }
              className={classNames(css.LocationInputItem, css.LocationInputClipboard)}
              onClick={getClipboard}
              before={<ClipboardIcon size="18" />}
            >
              <Text className={css.LocationInputField}>Paste Clipboard</Text>
            </Chip>
          </Box>
          {locationError !== LocationErrors.none && (
            <Box className={css.LocationDialogErrorText}>
              {chipIcon(Warning)}
              <Text size="L400">{locationError}</Text>
            </Box>
          )}
          <Box direction="Row" gap="100" className={css.LocationInputs}>
            <Box direction="Column" className={css.LocationInputItem}>
              <Text size="T200">Latitude</Text>
              <Input
                className={css.LocationInputField}
                variant={'SurfaceVariant'}
                size="300"
                radii="300"
                type="number"
                min={-180}
                max={180}
                value={inputPosition.lat}
                onChange={handleLat}
                outlined
              />
            </Box>
            <Box direction="Column" className={css.LocationInputItem}>
              <Text size="T200">Longitude</Text>
              <Input
                className={css.LocationInputField}
                variant={'SurfaceVariant'}
                size="300"
                radii="300"
                type="number"
                min={-180}
                max={180}
                value={inputPosition.lng}
                onChange={handleLng}
                outlined
              />
            </Box>
          </Box>
          {showMaps && (
            <Box className={css.LocationMapBody}>
              <MapContainer
                center={initCoords}
                zoom={zoom.current}
                scrollWheelZoom={true}
                className={css.LocationMapContainer}
                ref={setMap}
              >
                <TileLayer attribution={TILE_ATTRIBUTION} url={getTileUrl()} />
                <Marker
                  position={initCoords}
                  eventHandlers={{
                    mousedown: (e) => {
                      e.originalEvent.preventDefault();
                      e.originalEvent.stopPropagation();
                    },
                  }}
                  icon={markerIcon}
                />

                <MapEvents />
              </MapContainer>
            </Box>
          )}
          <Button
            type="submit"
            variant="Primary"
            title="Share Location"
            aria-label="Share Location"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <Text size="B400">Share Location</Text>
          </Button>
          {!!submitError && (
            <Text align="Center" size="B500" style={{ color: color.Critical.OnContainer }}>
              {submitError}
            </Text>
          )}
        </Box>
      </Dialog>
    </ModalOverlay>
  );
}
